/* ==========================================================================
   Pawsome Motion Layer
   Dependency-free scroll animation engine (no GSAP, no jQuery).
   - Reveal-on-scroll across every template
   - Word-by-word heading reveals
   - Scroll-scrubbed story section (sticky acts, drawn route, flying cards)
   - Parallax media, progress bar, count-ups, pointer-reactive buttons
   Respects prefers-reduced-motion and re-initialises after Shopify
   section renders and view transitions.
   ========================================================================== */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  var root = document.documentElement;

  /* Mark ready immediately so the CSS no-JS fallback stops applying. */
  root.classList.add('pw-motion-ready');
  document.body && document.body.classList.add('pw-motion-on');

  if (REDUCED.matches) {
    // Nothing else to do — CSS handles the static presentation.
    return;
  }

  /* ------------------------------------------------------------------
     Shared rAF ticker. One loop for every scroll-linked effect.
     ------------------------------------------------------------------ */
  var tasks = [];
  var ticking = false;

  function addTask(fn) {
    if (tasks.indexOf(fn) === -1) tasks.push(fn);
  }

  function tick() {
    for (var i = 0; i < tasks.length; i++) {
      try {
        tasks[i]();
      } catch (e) {
        /* keep the loop alive if one effect fails */
      }
    }
    ticking = false;
  }

  function requestTick() {
    if (!ticking) {
      ticking = true;
      window.requestAnimationFrame(tick);
    }
  }

  /* Scroll events do not bubble, but they DO fire on `document` during the
     capture phase for ANY scrolling element. Some themes (Horizon included)
     scroll an inner wrapper div rather than the document, in which case a
     plain `window` scroll listener never fires. Capturing on `document`
     catches every scroller without having to know which one it is. */
  var sawScrollEvent = false;
  function onScroll() {
    sawScrollEvent = true;
    requestTick();
  }
  document.addEventListener('scroll', onScroll, { passive: true, capture: true });
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', function () {
    measureCache = new WeakMap();
    requestTick();
  });

  /* Fallback for scroll containers driven by transforms or smooth-scroll
     libraries that emit no scroll event at all. This is a probe, not a
     permanent loop: as soon as a real scroll event arrives we shut it down,
     so the steady state costs zero frames. */
  var probeUntil = 0;
  var lastProbe = null;
  function watchdog(now) {
    if (sawScrollEvent) return; // real events are flowing — stop polling
    if (!probeUntil) probeUntil = now + 6000;
    if (now > probeUntil) return; // nothing scrolls here; give up
    var probe = document.querySelector('.pw-story');
    var key = probe ? Math.round(probe.getBoundingClientRect().top) : null;
    if (key !== lastProbe) {
      lastProbe = key;
      requestTick();
    }
    window.requestAnimationFrame(watchdog);
  }
  window.requestAnimationFrame(watchdog);

  var measureCache = new WeakMap();

  /* Find the element that actually scrolls. Falls back to the document. */
  var scrollerEl = null;
  function getScroller() {
    if (scrollerEl && scrollerEl.isConnected) return scrollerEl;
    var de = document.scrollingElement || document.documentElement;
    if (de.scrollHeight - de.clientHeight > 40) {
      scrollerEl = de;
      return scrollerEl;
    }
    var node = document.querySelector('.pw-story') || document.getElementById('MainContent') || document.body;
    while (node && node !== document.documentElement) {
      var cs = getComputedStyle(node);
      var oy = cs.overflowY;
      if ((oy === 'auto' || oy === 'scroll' || oy === 'overlay') && node.scrollHeight - node.clientHeight > 40) {
        scrollerEl = node;
        return scrollerEl;
      }
      node = node.parentElement;
    }
    scrollerEl = de;
    return scrollerEl;
  }

  function clamp01(v) {
    return v < 0 ? 0 : v > 1 ? 1 : v;
  }

  /* ==================================================================
     1. REVEAL ON SCROLL
     ================================================================== */

  var revealObserver = null;

  /* Map of observed proxy element -> targets to reveal.
     A proxy is needed for clip-path reveals: a fully clipped element has a
     zero-area paint box, so IntersectionObserver never fires on it. We watch
     its parent instead and reveal the child. */
  var proxyTargets = new WeakMap();

  function getRevealObserver() {
    if (revealObserver) return revealObserver;
    revealObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var targets = proxyTargets.get(entry.target);
          if (targets && targets.length) {
            targets.forEach(function (t) {
              t.classList.add('pw-in');
            });
            proxyTargets.delete(entry.target);
          } else {
            entry.target.classList.add('pw-in');
          }
          revealObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px 40px 0px', threshold: 0 }
    );
    return revealObserver;
  }

  function observeReveal(el, proxy) {
    if (el.dataset.pwObserved) return;
    el.dataset.pwObserved = '1';

    if (proxy && proxy !== el) {
      var list = proxyTargets.get(proxy);
      if (list) {
        list.push(el);
        return; // proxy already observed
      }
      proxyTargets.set(proxy, [el]);
      getRevealObserver().observe(proxy);
      return;
    }
    getRevealObserver().observe(el);
  }

  /* Which elements get which reveal, per region. Order matters — first match wins. */
  var REVEAL_RULES = [
    { sel: '[data-pw]', attr: null }, // author-specified via data-pw="up|fade|..."
    { sel: '.pw-story h2, .pw-story .pw-display', attr: 'skip' },
    { sel: 'h1, h2', attr: 'split' },
    { sel: 'h3, h4', attr: 'up' },
    { sel: '.product-card, .card-gallery__inner, .resource-card', attr: 'up' },
    { sel: '.rte > p, .prose > p, .rte > ul, .rte > ol, .rte > blockquote', attr: 'up' },
    { sel: '.rte > table, .prose > table', attr: 'up' },
    { sel: 'media-gallery img, .banner__media, .image-block, figure > img', attr: 'scale' },
    { sel: '.button, a.button', attr: 'blur' },
    { sel: '.marquee, marquee-component', attr: 'fade' }
  ];

  /* Regions we never animate: chrome, forms, cart, checkout-critical UI. */
  var SKIP_CLOSEST = [
    'header',
    '#header-group',
    'footer',
    'nav',
    'cart-drawer-component',
    '#cart-drawer',
    'dialog',
    '.header',
    '.footer',
    '.cart-drawer',
    '.quick-add-modal',
    '.predictive-search',
    'form[action*="/cart"]',
    '.shopify-payment-button',
    '.pw-no-motion'
  ].join(',');

  function isSkipped(el) {
    if (el.closest(SKIP_CLOSEST)) return true;
    if (el.dataset.pwReveal) return true;
    if (el.dataset.pwObserved) return true;
    // Don't animate something whose ancestor is already animating.
    if (el.parentElement && el.parentElement.closest('[data-pw-reveal]')) return true;
    return false;
  }

  function applyReveals(scope) {
    scope = scope || document;

    REVEAL_RULES.forEach(function (rule) {
      var nodes = scope.querySelectorAll(rule.sel);
      var groupIndex = 0;
      var lastParent = null;

      for (var i = 0; i < nodes.length; i++) {
        var el = nodes[i];
        if (isSkipped(el)) continue;

        var mode = rule.attr;
        if (mode === 'skip') {
          el.dataset.pwReveal = '';
          el.dataset.pwObserved = '1';
          continue;
        }
        if (rule.attr === null) {
          mode = el.dataset.pw || 'up';
        }

        // Stagger siblings that share a parent.
        if (el.parentElement !== lastParent) {
          groupIndex = 0;
          lastParent = el.parentElement;
        }
        el.style.setProperty('--pw-i', String(Math.min(groupIndex, 8)));
        groupIndex++;

        if (mode === 'split') {
          splitHeading(el);
        } else {
          el.setAttribute('data-pw-reveal', mode);
          // clip-path reveals must be triggered from an unclipped ancestor
          observeReveal(el, mode === 'mask' ? el.parentElement : null);
        }
      }
    });
  }

  /* ==================================================================
     2. SPLIT HEADINGS — word-by-word mask reveal
     ================================================================== */

  function splitHeading(el, observe) {
    if (observe === undefined) observe = true;
    if (el.dataset.pwSplitDone) return;
    // Only split plain-ish headings; bail if it holds interactive markup.
    if (el.querySelector('a, button, input, svg, img, select')) {
      el.setAttribute('data-pw-reveal', 'up');
      observeReveal(el);
      return;
    }
    var text = el.textContent.replace(/\s+/g, ' ').trim();
    if (!text || text.length > 160) {
      el.setAttribute('data-pw-reveal', 'up');
      observeReveal(el);
      return;
    }

    var words = text.split(' ');
    var frag = document.createDocumentFragment();

    words.forEach(function (word, i) {
      var outer = document.createElement('span');
      outer.className = 'pw-word';
      outer.style.setProperty('--pw-wi', String(i));
      var inner = document.createElement('span');
      inner.textContent = word;
      outer.appendChild(inner);
      frag.appendChild(outer);
      if (i < words.length - 1) frag.appendChild(document.createTextNode(' '));
    });

    el.textContent = '';
    el.appendChild(frag);
    el.classList.add('pw-split');
    el.dataset.pwSplitDone = '1';
    el.dataset.pwObserved = '1';

    if (observe) getRevealObserver().observe(el);
  }

  /* ==================================================================
     3. SCROLL PROGRESS BAR
     ================================================================== */

  function initProgress() {
    if (document.querySelector('.pw-progress')) return;
    var wrap = document.createElement('div');
    wrap.className = 'pw-progress';
    wrap.setAttribute('aria-hidden', 'true');
    var bar = document.createElement('div');
    bar.className = 'pw-progress__bar';
    wrap.appendChild(bar);
    document.body.appendChild(wrap);

    addTask(function () {
      var sc = getScroller();
      var max = sc.scrollHeight - sc.clientHeight;
      var p = max > 0 ? clamp01(sc.scrollTop / max) : 0;
      bar.style.transform = 'scaleX(' + p.toFixed(4) + ')';
    });
  }

  /* ==================================================================
     4. PARALLAX MEDIA
     ================================================================== */

  function initParallax(scope) {
    scope = scope || document;
    var candidates = scope.querySelectorAll('[data-pw-parallax]');
    if (!candidates.length) return;

    var items = Array.prototype.slice.call(candidates);

    addTask(function () {
      var vh = window.innerHeight;
      items.forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (r.bottom < -200 || r.top > vh + 200) return;
        var depth = parseFloat(el.dataset.pwParallax) || 12;
        // -1 .. 1 across the viewport
        var centred = (r.top + r.height / 2 - vh / 2) / (vh / 2);
        el.style.setProperty('--pw-y', (centred * depth).toFixed(2) + 'px');
      });
    });
  }

  /* ==================================================================
     5. GENERIC SCRUB SECTIONS  [data-pw-scrub]
     Writes --p = 0..1 as the element travels through the viewport.
     ================================================================== */

  function initScrub(scope) {
    scope = scope || document;
    var nodes = scope.querySelectorAll('[data-pw-scrub]');
    if (!nodes.length) return;
    var items = Array.prototype.slice.call(nodes);

    addTask(function () {
      var vh = window.innerHeight;
      items.forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (r.bottom < 0 || r.top > vh) return;
        var total = r.height + vh;
        var p = clamp01((vh - r.top) / total);
        el.style.setProperty('--p', p.toFixed(4));
      });
    });
  }

  /* ==================================================================
     6. COUNT-UP FIGURES  [data-pw-count="1200"]
     ================================================================== */

  function initCounts(scope) {
    scope = scope || document;
    var nodes = scope.querySelectorAll('[data-pw-count]');
    if (!nodes.length) return;

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          io.unobserve(el);
          var target = parseFloat(el.dataset.pwCount) || 0;
          var prefix = el.dataset.pwPrefix || '';
          var suffix = el.dataset.pwSuffix || '';
          var dp = parseInt(el.dataset.pwDecimals || '0', 10);
          var start = performance.now();
          var dur = 1500;

          function step(now) {
            var t = clamp01((now - start) / dur);
            var eased = 1 - Math.pow(1 - t, 3);
            el.textContent = prefix + (target * eased).toFixed(dp) + suffix;
            if (t < 1) window.requestAnimationFrame(step);
          }
          window.requestAnimationFrame(step);
        });
      },
      { threshold: 0.5 }
    );

    Array.prototype.forEach.call(nodes, function (el) {
      el.classList.add('pw-count');
      io.observe(el);
    });
  }

  /* ==================================================================
     7. POINTER-REACTIVE BUTTONS
     ================================================================== */

  function initPointer() {
    if (!window.matchMedia('(hover: hover)').matches) return;
    document.addEventListener(
      'pointermove',
      function (e) {
        var btn = e.target.closest && e.target.closest('.button');
        if (!btn) return;
        var r = btn.getBoundingClientRect();
        btn.style.setProperty('--pw-mx', (e.clientX - r.left).toFixed(0) + 'px');
        btn.style.setProperty('--pw-my', (e.clientY - r.top).toFixed(0) + 'px');
      },
      { passive: true }
    );
  }

  /* ==================================================================
     8. THE STORY SECTION — sticky, scroll-scrubbed acts
     ================================================================== */

  function initStory(scope) {
    scope = scope || document;
    var stories = scope.querySelectorAll('.pw-story');
    if (!stories.length) return;

    Array.prototype.forEach.call(stories, function (story) {
      if (story.dataset.pwStoryReady) return;
      story.dataset.pwStoryReady = '1';

      var track = story.querySelector('.pw-story__track');
      var acts = Array.prototype.slice.call(story.querySelectorAll('.pw-act'));
      if (!track || !acts.length) return;

      /* Split every act heading. These are driven by .is-live, not by the
         viewport observer, so the words replay each time an act comes up. */
      Array.prototype.forEach.call(story.querySelectorAll('.pw-act h2'), function (h) {
        splitHeading(h, false);
      });

      var counterNow = story.querySelector('[data-pw-counter-now]');
      var counterAll = story.querySelector('[data-pw-counter-all]');
      if (counterAll) counterAll.textContent = String(acts.length).padStart(2, '0');

      /* Prepare the drawn mark(s): set --len from real path length. */
      Array.prototype.forEach.call(story.querySelectorAll('.pw-mark path, .pw-mark circle, .pw-mark ellipse'), function (p) {
        var len = 300;
        try {
          len = p.getTotalLength();
        } catch (e) {}
        p.style.setProperty('--len', Math.ceil(len + 2));
      });

      /* Route path + travelling product */
      var routeLine = story.querySelector('.pw-route__line');
      var vehicle = story.querySelector('.pw-route__vehicle');
      var routeWrap = story.querySelector('.pw-route');
      var routeLen = 0;
      if (routeLine) {
        try {
          routeLen = routeLine.getTotalLength();
        } catch (e) {
          routeLen = 1000;
        }
        routeLine.style.setProperty('--len', Math.ceil(routeLen + 2));
      }

      var lastAct = -1;

      addTask(function () {
        var vh = window.innerHeight;
        var r = track.getBoundingClientRect();
        var scrollable = r.height - vh;
        if (scrollable <= 0) return;

        var raw = -r.top / scrollable;
        var p = clamp01(raw);

        story.style.setProperty('--p', p.toFixed(4));
        story.classList.toggle('is-moving', p > 0.02);
        story.classList.toggle('is-past', p > 0.98);

        /* Let the film play full-bleed: pull the sticky header out of the way
           while the story owns the viewport, then hand it back. */
        var inFilm = r.top <= 1 && r.bottom > vh * 0.9;
        root.classList.toggle('pw-chrome-hidden', inFilm);

        /* ---- Which act is live? Equal slices with a crossfade band. ---- */
        var slice = 1 / acts.length;
        var idx = Math.min(acts.length - 1, Math.floor(p / slice));
        var actP = clamp01((p - idx * slice) / slice);

        if (idx !== lastAct) {
          acts.forEach(function (a, i) {
            a.classList.toggle('is-live', i === idx);
            a.setAttribute('aria-hidden', i === idx ? 'false' : 'true');
          });
          if (counterNow) counterNow.textContent = String(idx + 1).padStart(2, '0');
          lastAct = idx;
        }

        acts[idx].style.setProperty('--ap', actP.toFixed(4));

        /* ---- Route drawing + product travelling along it ---- */
        if (routeLine && routeWrap) {
          var routeAct = parseInt(routeWrap.closest('.pw-act').dataset.actIndex || '0', 10);
          // Draw progress is tied to the act that owns the route.
          var draw = idx < routeAct ? 0 : idx > routeAct ? 1 : actP;
          // ease so the line finishes drawing before the act ends
          var d = clamp01(draw / 0.85);
          routeLine.style.setProperty('--draw', d.toFixed(4));

          if (vehicle && routeLen) {
            var pt;
            try {
              pt = routeLine.getPointAtLength(routeLen * d);
            } catch (e) {
              pt = null;
            }
            if (pt) {
              // viewBox is 1600x900, container is aspect-ratio 16/9 → uniform scale
              var scale = routeWrap.clientWidth / 1600;
              vehicle.style.setProperty('--vx', (pt.x * scale).toFixed(1) + 'px');
              vehicle.style.setProperty('--vy', (pt.y * scale).toFixed(1) + 'px');
              vehicle.style.opacity = d > 0.02 && d < 0.995 ? '1' : d >= 0.995 ? '1' : '0';
            }
          }
        }
      });
    });
  }

  /* ==================================================================
     BOOT + RE-BOOT
     ================================================================== */

  function boot(scope) {
    document.body && document.body.classList.add('pw-motion-on');
    applyReveals(scope);
    initParallax(scope);
    initScrub(scope);
    initCounts(scope);
    initStory(scope);
    try {
      initCart();
    } catch (e) {
      /* cart markup varies by theme version — never block the rest */
    }
    requestTick();
  }

  /* ==================================================================
     9. CART & PRE-CHECKOUT MOTION
     Shopify's checkout is hosted by Shopify and takes no custom CSS/JS
     below Plus, so the buying flow is animated up to the checkout button:
     drawer open, line-item cascade, total changes, bubble bumps, quantity
     presses and add-to-cart confirmation.
     ================================================================== */

  function flash(el, cls, ms) {
    if (!el) return;
    el.classList.remove(cls);
    /* force a style flush so the animation can restart on rapid clicks */
    void el.offsetWidth;
    el.classList.add(cls);
    window.setTimeout(function () {
      el.classList.remove(cls);
    }, ms);
  }

  function initCart() {
    /* PW premium cart-page entrance */
    if (document.querySelector('.cart-page')) {
      window.requestAnimationFrame(function () {
        document.documentElement.classList.add('pw-cart-page-ready');
      });
    }
    /* ---- Drawer opening: index the line items, arm the sheen ---- */
    function onDrawerOpen(dialog) {
      var wrap = dialog.querySelector('.cart-drawer__inner') || dialog;
      var items = dialog.querySelectorAll('.cart-drawer__items > *');
      for (var i = 0; i < items.length; i++) {
        items[i].style.setProperty('--pw-i', i);
      }
      wrap.classList.remove('pw-cart-open');
      void wrap.offsetWidth;
      wrap.classList.add('pw-cart-open');
    }

    /* The drawer node is replaced on every cart update, so re-attach. */
    function attachDialogs() {
      var dialogs = document.querySelectorAll('dialog.theme-drawer__dialog');
      for (var d = 0; d < dialogs.length; d++) {
        (function (dialog) {
          if (dialog.dataset.pwCart) return;
          dialog.dataset.pwCart = '1';
          if (dialog.open) onDrawerOpen(dialog);
          new MutationObserver(function () {
            if (dialog.open) onDrawerOpen(dialog);
          }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
        })(dialogs[d]);
      }
    }
    attachDialogs();
    window.__pwAttachDialogs = attachDialogs;

    /* ---- React to cart totals and badge changes ----
       The drawer and totals are re-rendered wholesale by the Section
       Rendering API, so an observer bound to a specific node dies on the
       first update. Instead: intercept cart requests once, then re-query
       and compare text at a few points while the new markup settles. */
    if (!window.__pwCartHooked) {
      window.__pwCartHooked = true;

      var lastSeen = {};
      var WATCH = [
        { sel: '.cart-totals__total', cls: 'pw-total', ms: 420 },
        { sel: '.cart-bubble__text-count', cls: null, ms: 460 },
      ];

      /* Prime the baseline so the first real change is what animates. */
      function readAll(store) {
        for (var i = 0; i < WATCH.length; i++) {
          var el = document.querySelector(WATCH[i].sel);
          store[WATCH[i].sel] = el ? el.textContent.trim() : null;
        }
      }
      readAll(lastSeen);

      function checkChanges() {
        for (var i = 0; i < WATCH.length; i++) {
          var w = WATCH[i];
          var el = document.querySelector(w.sel);
          if (!el) continue;
          var now = el.textContent.trim();
          if (now === lastSeen[w.sel] || lastSeen[w.sel] == null) {
            lastSeen[w.sel] = now;
            continue;
          }
          lastSeen[w.sel] = now;
          if (w.cls) {
            el.classList.add(w.cls);
            flash(el, 'pw-bump', w.ms);
          } else {
            flash(el.closest('.cart-bubble') || el, 'pw-bump', w.ms);
          }
        }
      }

      function scheduleChecks() {
        [60, 200, 450, 800, 1300].forEach(function (t) {
          window.setTimeout(function () {
            checkChanges();
            if (window.__pwAttachDialogs) window.__pwAttachDialogs();
          }, t);
        });
      }

      var nativeFetch = window.fetch;
      if (typeof nativeFetch === 'function') {
        window.fetch = function () {
          var url = '';
          try {
            var a = arguments[0];
            url = typeof a === 'string' ? a : a && a.url ? a.url : '';
          } catch (e) {
            url = '';
          }
          var p = nativeFetch.apply(this, arguments);
          if (url && url.indexOf('/cart') !== -1) {
            p.then(scheduleChecks, function () {});
          }
          return p;
        };
      }

      /* Belt and braces: the theme also fires its own cart events. */
      ['cart:update', 'cart:updated', 'CartUpdateEvent', 'cart:refresh'].forEach(
        function (name) {
          document.addEventListener(name, scheduleChecks);
        }
      );
    }

    /* ---- Delegated presses: quantity steppers and add-to-cart ---- */
    if (!document.body.dataset.pwCartClicks) {
      document.body.dataset.pwCartClicks = '1';
      document.addEventListener(
        'click',
        function (e) {
          var t = e.target;
          if (!t || !t.closest) return;

          /* PW secure checkout handoff */
          var checkoutTrigger = t.closest('button[name="checkout"], [href*="/checkout"]');
          if (checkoutTrigger) {
            document.documentElement.classList.add('pw-checkout-launch');
            checkoutTrigger.classList.add('pw-checkout-launching');
            checkoutTrigger.setAttribute('aria-busy', 'true');
            window.setTimeout(function () {
              document.documentElement.classList.remove('pw-checkout-launch');
              checkoutTrigger.classList.remove('pw-checkout-launching');
              checkoutTrigger.removeAttribute('aria-busy');
            }, 3200);
            return;
          }

          var step = t.closest('.quantity-minus, .quantity-plus');
          if (step) {
            flash(step, 'pw-pop', 260);
            flash(step.closest('.quantity-selector'), 'pw-flash', 440);
            return;
          }

          var atc = t.closest('button[type="submit"], .button');
          if (!atc) return;
          var form = atc.closest('form[action*="/cart/add"], product-form-component');
          if (form) flash(atc, 'pw-atc-added', 660);
        },
        true
      );
    }
  }

  function bootAll() {
    initProgress();
    initPointer();
    initCart();
    boot(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll, { once: true });
  } else {
    bootAll();
  }

  /* Shopify theme editor: sections re-render without a page load. */
  document.addEventListener('shopify:section:load', function (e) {
    boot(e.target || document);
  });

  /* Horizon uses cross-document view transitions + section hydration. */
  window.addEventListener('pageshow', function () {
    boot(document);
  });
  document.addEventListener('shopify:block:select', function (e) {
    boot(e.target || document);
  });

  /* Catch content injected later (infinite scroll, quick add, recommendations). */
  var mo = new MutationObserver(function (mutations) {
    var needs = false;
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        if (added[j].nodeType === 1) {
          needs = true;
          break;
        }
      }
      if (needs) break;
    }
    if (needs) {
      clearTimeout(mo._t);
      mo._t = setTimeout(function () {
        applyReveals(document);
        requestTick();
      }, 120);
    }
  });

  if (document.getElementById('MainContent')) {
    mo.observe(document.getElementById('MainContent'), { childList: true, subtree: true });
  }

  /* Expose for debugging / future sections. */
  window.PawsomeMotion = {
    refresh: function () {
      boot(document);
    },
    addTask: addTask
  };
})();
